import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, mutate } from '../shared/api';
import { readUrlParam, useUrlSync } from '../shared/urlState';
import { DateInput } from '../shared/DateInput';

/**
 * m.11 Tasks — Asana funkcionalni klon.
 * 3 view-a po projektu (List / Board / Calendar) + My Tasks side panel + Task drawer.
 * Single-file modul (~700 LOC). Backbone: TasksController.
 */

type Locale = 'hr' | 'en';
type Project = {
    id: number; workspace_id: number; name: string; description: string | null;
    color: string; icon: string; view_default: string; status: string;
    due_date: string | null; owner_user_id: number | null; owner_name: string | null;
    sort_order: number; is_favorite: boolean | number;
    task_count: number; completed_count: number;
    asana_project_gid?: string | null;
};
type Section = { id: number; project_id: number; name: string; sort_order: number };
type Task = {
    id: number; project_id: number; section_id: number | null; parent_task_id: number | null;
    title: string; description: string | null;
    assignee_user_id: number | null; assignee_name: string | null;
    due_date: string | null; start_date: string | null;
    completed_at: string | null; priority: string;
    sort_order: number; subtask_count?: number; comment_count?: number;
    asana_gid?: string | null; asana_permalink_url?: string | null;
};
type Member = { id: number; name: string; email: string; role?: string };
type StateResp = {
    workspace: { id: number; name: string; color: string };
    members: Member[];
    all_users: Member[];
    tags: Array<{ id: number; name: string; color: string }>;
};
type MyTasksResp = { overdue: Task[]; today: Task[]; upcoming: Task[]; no_date: Task[] };
type CalRow = { id: number; title: string; priority: string; due_date: string; completed_at: string | null; project_id: number; project_name: string; project_color: string; project_icon: string; assignee_name: string | null };

const BROWN = 'var(--ad-ink)';
const GOLD = 'var(--ad-acc)';
const PRIORITY_META: Record<string, { hr: string; en: string; color: string; icon: string }> = {
    low:    { hr: 'Nisko',  en: 'Low',    color: '#8a8a8a', icon: '▽' },
    normal: { hr: 'Srednje', en: 'Normal', color: '#1e40af', icon: '─' },
    high:   { hr: 'Visoko', en: 'High',   color: 'var(--ad-acc-3)', icon: '▲' },
    urgent: { hr: 'HITNO',  en: 'Urgent', color: '#b14040', icon: '🔥' },
};
const fmtDate = (s: string | null): string => {
    if (!s) return '';
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    return m ? `${m[3]}.${m[2]}.${m[1]}.` : s;
};
const todayIso = () => new Date().toISOString().slice(0, 10);

type View = 'list' | 'board' | 'calendar';
type LeftTab = 'projects' | 'mytasks' | 'dechaos';

type DcTask = { id: number; title: string; notes: string | null; due_at: string | null; priority: number; category: string; estimated_duration_minutes: number | null; waiting_for: string | null; waiting_since: string | null; waiting_snoozed_until: string | null; status?: string; completed_at?: string | null };
type DcToday = { overdue: DcTask[]; today: DcTask[]; tomorrow: DcTask[]; this_week: DcTask[]; later: DcTask[]; waiting: DcTask[]; no_date: DcTask[] };
type DcProject = { id: number; title: string; slug: string; status: string; task_count: number; open_count: number; target_date: string | null };
type DcHealth = { configured: boolean; reachable?: boolean; as_user?: string; error?: string | null };

export function TasksModule({ locale, token, onBack, onOpenTeamTasks }: { locale: Locale; token: string | null; onBack?: () => void; dechaosEnabled?: boolean; onOpenTeamTasks?: () => void }) {
    // m.11.1 Team Tasks je zaseban modul; DeChaos potpuno ugašen.
    const dechaosEnabled = false;
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [state, setState] = useState<StateResp | null>(null);
    const [projects, setProjects] = useState<Project[]>([]);
    const [activeProject, setActiveProject] = useState<Project | null>(null);
    const [view, setView] = useState<View>('list');
    const [sections, setSections] = useState<Section[]>([]);
    const [tasks, setTasks] = useState<Task[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [leftTab, setLeftTab] = useState<LeftTab>(readUrlParam('tab', 'projects') as LeftTab);
    useUrlSync('tab', leftTab, leftTab === 'projects');
    const [myTasks, setMyTasks] = useState<MyTasksResp | null>(null);
    const [calendar, setCalendar] = useState<CalRow[]>([]);
    const [calMonth, setCalMonth] = useState<Date>(new Date());
    const [showNewProject, setShowNewProject] = useState(false);
    const [openTaskId, setOpenTaskId] = useState<number | null>(null);
    // DeChaos Task2 state
    const [dcHealth, setDcHealth] = useState<DcHealth | null>(null);
    const [dcToday, setDcToday] = useState<DcToday | null>(null);
    const [dcProjects, setDcProjects] = useState<DcProject[]>([]);
    const [dcActiveProject, setDcActiveProject] = useState<DcProject | null>(null);
    const [dcProjectTasks, setDcProjectTasks] = useState<DcTask[]>([]);
    const [dcView, setDcView] = useState<'wrap' | 'today2' | 'projects' | 'project_detail' | 'waiting' | 'triage' | 'settings'>('wrap');
    const [dcTriage, setDcTriage] = useState<{ count: number; rows: any[] } | null>(null);
    const [dcTriageBusy, setDcTriageBusy] = useState<number | null>(null);
    const [dcSettings, setDcSettings] = useState<any>(null);
    const [dcSettingsDraft, setDcSettingsDraft] = useState<any>(null);
    const loadDcTriage = useCallback(async () => {
        if (!token) return;
        try { setDcTriage(await api<any>('/dechaos/triage', token)); } catch (e) { setError(String(e)); }
    }, [token]);
    const dcApprove = async (id: number) => {
        if (!token) return;
        setDcTriageBusy(id);
        try { await mutate(`/dechaos/triage/${id}/approve`, token, 'POST', {}); await loadDcTriage(); await loadDcAll(); } catch (e) { setError(String(e)); } finally { setDcTriageBusy(null); }
    };
    const dcDiscard = async (id: number) => {
        if (!token) return;
        setDcTriageBusy(id);
        try { await mutate(`/dechaos/triage/${id}/discard`, token, 'POST', {}); await loadDcTriage(); } catch (e) { setError(String(e)); } finally { setDcTriageBusy(null); }
    };
    const loadDcSettings = useCallback(async () => {
        if (!token) return;
        try { const r = await api<any>('/dechaos/settings', token); setDcSettings(r); setDcSettingsDraft(r?.user ?? null); } catch (e) { setError(String(e)); }
    }, [token]);
    const saveDcSettings = async () => {
        if (!token || !dcSettingsDraft) return;
        try {
            const body: any = {};
            ['name', 'locale', 'timezone', 'theme', 'inbound_local_part'].forEach((f) => {
                if (dcSettingsDraft[f] !== undefined && dcSettingsDraft[f] !== dcSettings?.user?.[f]) body[f] = dcSettingsDraft[f];
            });
            await mutate('/dechaos/settings', token, 'PATCH', body);
            await loadDcSettings();
        } catch (e) { setError(String(e)); }
    };
    const [dcWrapUrl, setDcWrapUrl] = useState<string | null>(null);
    const [dcWrapBusy, setDcWrapBusy] = useState(false);
    const [dcWrapTo, setDcWrapTo] = useState('/dashboard');
    const [dcWrapUser, setDcWrapUser] = useState<{ email: string; name: string; was_created: boolean } | null>(null);
    const loadDcWrap = useCallback(async (to: string = '/dashboard') => {
        if (!token) return;
        setDcWrapBusy(true);
        try {
            // SSO: auto-provision DeChaos user po BIS user-ovom emailu, dobij magic-link OTT
            const r = await api<{ url: string; user?: { id: number; email: string; name: string; was_created: boolean } }>(`/dechaos/sso?to=${encodeURIComponent(to)}`, token);
            setDcWrapUrl(r.url ?? null); setDcWrapTo(to);
            if (r.user) setDcWrapUser({ email: r.user.email, name: r.user.name, was_created: r.user.was_created });
        } catch (e) { setError(String(e)); }
        finally { setDcWrapBusy(false); }
    }, [token]);
    const [dcLoading, setDcLoading] = useState(false);
    const loadDcAll = useCallback(async () => {
        if (!token) return;
        setDcLoading(true);
        try {
            const h = await api<DcHealth>('/dechaos/health', token);
            setDcHealth(h);
            if (h.configured && h.reachable) {
                const [today, projs] = await Promise.all([
                    api<DcToday>('/dechaos/today', token),
                    api<{ projects: DcProject[] }>('/dechaos/projects', token),
                ]);
                setDcToday(today);
                setDcProjects(projs.projects ?? []);
            }
        } catch (e) { setError(String(e)); }
        finally { setDcLoading(false); }
    }, [token]);
    const loadDcProject = useCallback(async (p: DcProject) => {
        if (!token) return;
        setDcActiveProject(p); setDcView('project_detail');
        try {
            const r = await api<{ tasks: DcTask[] }>(`/dechaos/projects/${p.id}/tasks`, token);
            setDcProjectTasks(r.tasks ?? []);
        } catch (e) { setError(String(e)); }
    }, [token]);
    const dcComplete = async (taskId: number, reopen = false) => {
        if (!token) return;
        await mutate(`/dechaos/tasks/${taskId}/${reopen ? 'reopen' : 'complete'}`, token, 'POST', {});
        await loadDcAll();
        if (dcActiveProject) await loadDcProject(dcActiveProject);
    };
    const [search, setSearch] = useState('');
    const [searchResults, setSearchResults] = useState<Array<Task & { project_name: string; project_icon: string }>>([]);
    // Mobile responsive — sidebar je drawer ispod 800px
    const [isMobile, setIsMobile] = useState<boolean>(typeof window !== 'undefined' ? window.innerWidth < 800 : false);
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 800);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    // Intake (DeChaos brzi unos preko text/voice) — modal state
    const [showIntake, setShowIntake] = useState(false);

    const userById = useMemo(() => {
        const m: Record<number, Member> = {};
        (state?.all_users ?? []).forEach((u) => { m[u.id] = u; });
        return m;
    }, [state]);

    const loadState = useCallback(async () => {
        if (!token) return;
        try { setState(await api<StateResp>('/tasks/state', token)); } catch (e) { setError(String(e)); }
    }, [token]);
    const loadProjects = useCallback(async () => {
        if (!token) return;
        try {
            const r = await api<{ projects: Project[] }>('/tasks/projects', token);
            setProjects(r.projects);
        } catch (e) { setError(String(e)); }
    }, [token]);
    const loadProjectTasks = useCallback(async (p: Project) => {
        if (!token) return;
        setLoading(true);
        try {
            const r = await api<{ sections: Section[]; tasks: Task[] }>(`/tasks/projects/${p.id}/tasks`, token);
            setSections(r.sections); setTasks(r.tasks);
        } catch (e) { setError(String(e)); }
        finally { setLoading(false); }
    }, [token]);
    const loadMyTasks = useCallback(async () => {
        if (!token) return;
        try { setMyTasks(await api<MyTasksResp>('/tasks/my-tasks', token)); } catch (e) { setError(String(e)); }
    }, [token]);
    const loadCalendar = useCallback(async (month: Date) => {
        if (!token) return;
        const y = month.getFullYear(); const m = month.getMonth();
        const from = new Date(y, m, 1).toISOString().slice(0, 10);
        const to = new Date(y, m + 1, 0).toISOString().slice(0, 10);
        try {
            const r = await api<{ rows: CalRow[] }>(`/tasks/calendar?from=${from}&to=${to}`, token);
            setCalendar(r.rows);
        } catch (e) { setError(String(e)); }
    }, [token]);

    // Asana integration state (Phase B): per-user PAT konekcija s pull-engine sync
    const [asanaConn, setAsanaConn] = useState<{ connected: boolean; status?: string; default_workspace_gid?: string | null; default_workspace_name?: string | null; last_sync_at?: string | null; webhook?: { active: boolean; last_event_at?: string | null } | null } | null>(null);
    const [asanaSyncing, setAsanaSyncing] = useState(false);
    const [asanaSyncMsg, setAsanaSyncMsg] = useState<string>('');
    const loadAsanaConn = useCallback(async () => {
        if (!token) return;
        try { setAsanaConn(await api('/asana/connection', token)); }
        catch { setAsanaConn({ connected: false }); }
    }, [token]);
    const syncAsana = async () => {
        if (!token) return;
        setAsanaSyncing(true); setAsanaSyncMsg('');
        try {
            const r = await mutate<{ ok: boolean; projects: number; sections: number; tasks: number; skipped_conflicts: number; duration_ms: number }>('/asana/sync', token, 'POST', {});
            setAsanaSyncMsg(t(
                `Sync OK · ${r.projects} projekta · ${r.tasks} taskova · ${r.skipped_conflicts > 0 ? r.skipped_conflicts + ' konflikta preskočena · ' : ''}${(r.duration_ms / 1000).toFixed(1)}s`,
                `Sync OK · ${r.projects} projects · ${r.tasks} tasks · ${r.skipped_conflicts > 0 ? r.skipped_conflicts + ' conflicts skipped · ' : ''}${(r.duration_ms / 1000).toFixed(1)}s`,
            ));
            await loadProjects(); await loadMyTasks(); await loadAsanaConn();
            if (activeProject) await loadProjectTasks(activeProject);
        } catch (e) {
            setAsanaSyncMsg(t('Sync greška: ', 'Sync error: ') + (e instanceof Error ? e.message : 'API'));
        } finally { setAsanaSyncing(false); }
    };

    useEffect(() => { void loadState(); void loadProjects(); void loadMyTasks(); void loadAsanaConn(); if (dechaosEnabled) void loadDcAll(); }, [token, dechaosEnabled]);
    // Auto-issue magic-link kad user otvori DeChaos tab (wrap je default render za DeChaos tab)
    useEffect(() => { if (dechaosEnabled && leftTab === 'dechaos' && !dcWrapUrl && token && dcHealth?.reachable) void loadDcWrap(dcWrapTo); }, [leftTab, token, dcHealth?.reachable, dechaosEnabled]);
    // Ako user nema DeChaos enabled a leftTab je nekako postavljen na 'dechaos' (legacy state), forsiraj fallback
    useEffect(() => { if (!dechaosEnabled && leftTab === 'dechaos') setLeftTab('projects'); }, [dechaosEnabled, leftTab]);
    // Auto-load triage kad user uđe u DeChaos tab → ako ima pending, auto-prebaci na triage view
    useEffect(() => {
        if (leftTab !== 'dechaos' || !dcHealth?.reachable || !token) return;
        if (dcTriage === null) {
            void (async () => {
                try {
                    const r = await api<{ count: number; rows: any[] }>('/dechaos/triage', token);
                    setDcTriage(r);
                    if ((r?.count ?? 0) > 0 && dcView === 'wrap') setDcView('triage');
                } catch { /* silent */ }
            })();
        }
    }, [leftTab, dcHealth?.reachable, token]);
    useEffect(() => { if (leftTab === 'dechaos' && dcView === 'settings' && !dcSettings && token) void loadDcSettings(); }, [leftTab, dcView, token]);
    useEffect(() => { if (activeProject) void loadProjectTasks(activeProject); }, [activeProject?.id]);
    useEffect(() => { if (view === 'calendar' && !activeProject) void loadCalendar(calMonth); }, [view, calMonth, activeProject?.id]);

    // search debounce
    useEffect(() => {
        if (!token || search.trim().length < 2) { setSearchResults([]); return; }
        const id = setTimeout(async () => {
            try {
                const r = await api<{ rows: any[] }>(`/tasks/search?q=${encodeURIComponent(search.trim())}`, token);
                setSearchResults(r.rows);
            } catch { setSearchResults([]); }
        }, 300);
        return () => clearTimeout(id);
    }, [search, token]);

    const completeTask = async (taskId: number) => {
        if (!token) return;
        await mutate(`/tasks/tasks/${taskId}/complete`, token, 'POST', {});
        if (activeProject) await loadProjectTasks(activeProject);
        await loadMyTasks();
    };

    const newTaskInline = async (projectId: number, sectionId: number | null, title: string) => {
        if (!token || !title.trim()) return;
        await mutate('/tasks/tasks', token, 'POST', { project_id: projectId, section_id: sectionId, title: title.trim() });
        if (activeProject) await loadProjectTasks(activeProject);
    };

    const moveTask = async (taskId: number, toSectionId: number | null, toOrder?: number) => {
        if (!token) return;
        await mutate(`/tasks/tasks/${taskId}/move`, token, 'POST', { section_id: toSectionId, sort_order: toOrder });
        if (activeProject) await loadProjectTasks(activeProject);
    };

    // ── UI ──
    return (
        <section className="card admin-block" style={{ padding: 0 }}>
            {/* Header bar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: isMobile ? '8px 10px' : '10px 16px', borderBottom: '1px solid rgba(var(--ad-soft-rgb),0.3)', background: '#fffdf6', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                {isMobile ? (
                    <button type="button" onClick={() => setMobileSidebarOpen(true)} style={{ background: 'transparent', border: '1px solid var(--ad-acc)', color: BROWN, padding: '4px 10px', borderRadius: 5, cursor: 'pointer', fontSize: '1rem' }}>☰</button>
                ) : null}
                {onBack ? <button type="button" className="secondary compact" onClick={onBack} style={{ fontSize: '0.78rem' }}>←</button> : null}
                <h1 style={{ margin: 0, color: BROWN, fontSize: isMobile ? '1rem' : '1.2rem' }}>🗂 11 {isMobile ? '' : '· ' + t('Zadaci', 'Tasks')}</h1>
                {!isMobile ? <span className="muted" style={{ fontSize: '0.78rem' }}>{state?.workspace?.name ?? '—'}</span> : null}
                <div style={{ flex: 1 }} />
                {dechaosEnabled ? (
                    <button type="button" onClick={() => setShowIntake(true)}
                        title={t('Brzi unos (text / voice) → DeChaos AI', 'Quick intake (text / voice) → DeChaos AI')}
                        style={{ background: '#b14040', color: '#fff', border: 0, padding: isMobile ? '6px 12px' : '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: isMobile ? '0.86rem' : '0.84rem', fontWeight: 700 }}>
                        🧠 {isMobile ? '' : t('Brzi unos', 'Quick intake')}
                    </button>
                ) : null}
                {!isMobile ? (
                    <input type="text" placeholder={t('Pretraga zadataka…', 'Search tasks…')} value={search} onChange={(e) => setSearch(e.target.value)}
                        style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--ad-acc)', fontSize: '0.84rem', width: 240 }} />
                ) : null}
            </div>
            {isMobile ? (
                <div style={{ padding: '6px 10px', borderBottom: '1px solid rgba(var(--ad-soft-rgb),0.2)' }}>
                    <input type="text" placeholder={t('Pretraga zadataka…', 'Search tasks…')} value={search} onChange={(e) => setSearch(e.target.value)}
                        style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--ad-acc)', fontSize: '0.86rem' }} />
                </div>
            ) : null}

            {error ? <div style={{ padding: 10, background: '#fde8e8', color: '#b14040' }}>{error}</div> : null}

            {/* 3 glavna taba — horizontalni red iznad svega (vidljivi i u DeChaos full-width modu) */}
            <div style={{ display: 'flex', gap: 6, padding: isMobile ? '8px 10px' : '8px 16px', borderBottom: '1px solid rgba(var(--ad-soft-rgb),0.2)', flexWrap: 'wrap' }}>
                <button type="button" onClick={() => { setLeftTab('projects'); setActiveProject(null); setView('list'); }}
                    style={{ padding: '6px 16px', background: leftTab === 'projects' ? GOLD : 'transparent', color: leftTab === 'projects' ? '#fff' : BROWN, border: '1px solid var(--ad-acc)', borderRadius: 18, cursor: 'pointer', fontSize: '0.84rem', fontWeight: 700 }}>📁 {t('Projekti', 'Projects')}</button>
                <button type="button" onClick={() => { setLeftTab('mytasks'); setActiveProject(null); }}
                    style={{ padding: '6px 16px', background: leftTab === 'mytasks' ? GOLD : 'transparent', color: leftTab === 'mytasks' ? '#fff' : BROWN, border: '1px solid var(--ad-acc)', borderRadius: 18, cursor: 'pointer', fontSize: '0.84rem', fontWeight: 700 }}>👤 {t('Moji', 'Mine')}</button>
                {onOpenTeamTasks ? (
                    <button type="button" onClick={onOpenTeamTasks}
                        style={{ padding: '6px 16px', background: 'transparent', color: BROWN, border: '1px solid var(--ad-acc)', borderRadius: 18, cursor: 'pointer', fontSize: '0.84rem', fontWeight: 700 }}>👥 {t('Timski zadaci (m.11.1)', 'Team Tasks (m.11.1)')} →</button>
                ) : null}
            </div>

            <div style={{ display: 'flex', minHeight: 'calc(100vh - 240px)', position: 'relative' }}>
                {/* Mobile overlay backdrop */}
                {isMobile && mobileSidebarOpen && leftTab !== 'dechaos' ? (
                    <div onClick={() => setMobileSidebarOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 90 }} />
                ) : null}
                {/* Sidebar — SAMO za Projekti/Moji (DeChaos i Timovi su full-width) */}
                {leftTab !== 'dechaos' ? (
                <aside style={isMobile
                    ? { position: 'fixed', top: 0, bottom: 0, left: 0, width: 280, background: 'var(--ad-bg)', padding: 12, overflowY: 'auto', zIndex: 91, transform: mobileSidebarOpen ? 'translateX(0)' : 'translateX(-100%)', transition: 'transform 0.25s', boxShadow: mobileSidebarOpen ? '4px 0 20px rgba(0,0,0,0.25)' : 'none' }
                    : { width: 260, borderRight: '1px solid rgba(var(--ad-soft-rgb),0.3)', background: 'var(--ad-bg)', padding: 12, overflowY: 'auto' }}>
                {isMobile ? (
                    <button type="button" onClick={() => setMobileSidebarOpen(false)} style={{ position: 'absolute', top: 6, right: 6, background: 'transparent', border: 0, fontSize: '1.2rem', cursor: 'pointer', color: BROWN }}>✕</button>
                ) : null}

                    {leftTab === 'projects' ? (
                        <>
                            <button type="button" onClick={() => setShowNewProject(true)} style={{ width: '100%', padding: '6px 10px', background: '#1f7a36', color: '#fff', border: 0, borderRadius: 6, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 700, marginBottom: 6 }}>+ {t('Novi projekt', 'New project')}</button>
                            {asanaConn?.connected && asanaConn?.status === 'active' ? (
                                <div style={{ marginBottom: 10 }}>
                                    {asanaConn?.webhook?.active ? (
                                        // Webhook radi automatski — sync button postaje sekundarni (force re-sync)
                                        <>
                                            <div style={{ fontSize: '0.74rem', color: '#1f7a36', fontWeight: 700, padding: '3px 6px', background: 'rgba(31,122,54,0.08)', borderRadius: 4, marginBottom: 4 }}>
                                                ⚡ {t('Real-time sync aktivan — Asana automatski šalje promjene', 'Real-time sync active — Asana pushes changes automatically')}
                                            </div>
                                            <button type="button" disabled={asanaSyncing} onClick={() => void syncAsana()}
                                                style={{ width: '100%', padding: '4px 8px', background: 'transparent', color: 'var(--ad-ink)', border: '1px solid rgba(var(--ad-soft-rgb),0.5)', borderRadius: 6, cursor: asanaSyncing ? 'wait' : 'pointer', fontSize: '0.72rem', fontWeight: 600 }}>
                                                {asanaSyncing ? t('🔄 Resync u tijeku…', '🔄 Resyncing…') : t('↻ Force re-sync (rijetko potrebno)', '↻ Force re-sync (rarely needed)')}
                                            </button>
                                        </>
                                    ) : (
                                        // Webhook nije aktivan — manual sync je primary action
                                        <button type="button" disabled={asanaSyncing} onClick={() => void syncAsana()}
                                            style={{ width: '100%', padding: '6px 10px', background: asanaSyncing ? '#888' : '#f06a35', color: '#fff', border: 0, borderRadius: 6, cursor: asanaSyncing ? 'wait' : 'pointer', fontSize: '0.78rem', fontWeight: 700 }}>
                                            {asanaSyncing ? t('🔄 Sync u tijeku…', '🔄 Syncing…') : t('🟧 Sync s Asanom', '🟧 Sync from Asana')}
                                        </button>
                                    )}
                                    {asanaSyncMsg ? <div style={{ fontSize: '0.7rem', color: asanaSyncMsg.startsWith('Sync OK') ? '#1f7a36' : '#b14040', marginTop: 3, lineHeight: 1.3 }}>{asanaSyncMsg}</div> : null}
                                    {asanaConn.last_sync_at ? <div style={{ fontSize: '0.66rem', color: '#888', marginTop: 2 }}>{t('Zadnji sync:', 'Last sync:')} {String(asanaConn.last_sync_at).replace('T', ' ').slice(0, 19)}</div> : null}
                                </div>
                            ) : null}
                            {asanaConn?.connected && asanaConn?.status === 'invalid' ? (
                                <div style={{ fontSize: '0.72rem', color: '#b14040', padding: '4px 6px', background: 'rgba(177,64,64,0.08)', borderRadius: 4, marginBottom: 8 }}>
                                    ⚠ {t('Asana token nije valjan — re-spoji u profilu.', 'Asana token invalid — reconnect in profile.')}
                                </div>
                            ) : null}
                            {/* Calendar kao globalna opcija (sve projekte) */}
                            <button type="button" onClick={() => { setActiveProject(null); setView('calendar'); }}
                                style={{ width: '100%', textAlign: 'left', padding: '7px 10px', background: !activeProject && view === 'calendar' ? 'rgba(var(--ad-acc-rgb),0.18)' : 'transparent', border: 0, borderRadius: 6, cursor: 'pointer', fontSize: '0.86rem', marginBottom: 4, color: BROWN }}>📅 {t('Kalendar (svi)', 'Calendar (all)')}</button>
                            <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase', margin: '8px 4px 4px', letterSpacing: 0.5 }}>{t('Projekti', 'Projects')}</div>
                            {projects.length === 0 ? <p className="muted" style={{ fontSize: '0.78rem' }}>{t('Nema projekata', 'No projects')}</p> : null}
                            {projects.map((p) => (
                                <button key={p.id} type="button" onClick={() => { setActiveProject(p); setView(p.view_default as View); if (isMobile) setMobileSidebarOpen(false); }}
                                    style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left', padding: '7px 8px', background: activeProject?.id === p.id ? 'rgba(var(--ad-acc-rgb),0.18)' : 'transparent', border: 0, borderRadius: 6, cursor: 'pointer', fontSize: '0.86rem', marginBottom: 2, color: BROWN }}>
                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color, display: 'inline-block' }} />
                                    <span style={{ fontSize: '1rem' }}>{p.icon}</span>
                                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                                    {p.asana_project_gid ? <span title="Asana" style={{ fontSize: '0.7rem' }}>🟧</span> : null}
                                    <span style={{ fontSize: '0.66rem', color: '#888' }}>{p.completed_count}/{p.task_count}</span>
                                </button>
                            ))}
                        </>
                    ) : (
                        <MyTasksPanel mt={myTasks} onOpen={(id) => setOpenTaskId(id)} onComplete={completeTask} locale={locale} />
                    )}
                </aside>
                ) : null}

                {/* Main */}
                <main style={{ flex: 1, padding: isMobile ? 10 : 16, overflowY: 'auto', overflowX: 'auto', width: '100%' }}>
                    {search.trim().length >= 2 ? (
                        <SearchResults rows={searchResults} onOpen={(id) => setOpenTaskId(id)} locale={locale} />
                    ) : leftTab === 'dechaos' ? (
                        // PUNI DECHAOS — full-width direktni iframe (zauzima i lijevu i desnu)
                        !dcHealth ? <p className="muted" style={{ padding: 20 }}>{t('Provjeravam DeChaos…', 'Checking DeChaos…')}</p>
                        : !dcHealth.reachable ? (
                            <div style={{ padding: 20 }}>
                                <p style={{ color: '#b14040' }}>⚠ {t('DeChaos nije dostupan', 'DeChaos unavailable')}</p>
                                {dcHealth.error ? <pre style={{ fontSize: '0.74rem', color: '#888' }}>{dcHealth.error}</pre> : null}
                                <button type="button" onClick={() => void loadDcAll()} style={{ background: '#b14040', color: '#fff', border: 0, padding: '6px 14px', borderRadius: 5, cursor: 'pointer' }}>↻ {t('Pokušaj ponovo', 'Retry')}</button>
                            </div>
                        )
                        : !dcWrapUrl ? (
                            <div style={{ padding: 20, textAlign: 'center' }}>
                                <p className="muted">{t('Učitavam DeChaos…', 'Loading DeChaos…')} {dcWrapBusy ? '⏳' : ''}</p>
                                <button type="button" onClick={() => void loadDcWrap(dcWrapTo)} disabled={dcWrapBusy}
                                    style={{ background: 'var(--ad-ink)', color: '#fff', border: 0, padding: '6px 14px', borderRadius: 5, cursor: 'pointer' }}>↻ {t('Ponovo izdaj SSO link', 'Re-issue SSO link')}</button>
                            </div>
                        )
                        : (
                            <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 240px)', minHeight: 600, width: '100%' }}>
                                <iframe src={dcWrapUrl} title="DeChaos" allow="microphone *; camera *; clipboard-read *; clipboard-write *"
                                    sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals allow-downloads allow-presentation"
                                    style={{ flex: 1, width: '100%', border: 0, borderRadius: 0, background: '#fff' }} />
                            </div>
                        )
                    ) : !activeProject && view === 'calendar' ? (
                        <CalendarView rows={calendar} month={calMonth} setMonth={setCalMonth} onOpen={(id) => setOpenTaskId(id)} locale={locale} />
                    ) : !activeProject ? (
                        <EmptyState onCreate={() => setShowNewProject(true)} locale={locale} />
                    ) : (
                        <>
                            {/* Project header */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                                <span style={{ fontSize: '1.6rem' }}>{activeProject.icon}</span>
                                <h2 style={{ margin: 0, color: BROWN }}>{activeProject.name}</h2>
                                <span style={{ background: activeProject.color, color: '#fff', borderRadius: 4, padding: '2px 8px', fontSize: '0.7rem', fontWeight: 700 }}>{activeProject.status}</span>
                                <div style={{ flex: 1 }} />
                                <div style={{ display: 'flex', gap: 4, background: 'rgba(var(--ad-soft-rgb),0.15)', borderRadius: 6, padding: 2 }}>
                                    {(['list', 'board', 'calendar'] as View[]).map((v) => (
                                        <button key={v} type="button" onClick={() => { setView(v); if (v === 'calendar') void loadCalendar(calMonth); }}
                                            style={{ padding: '4px 14px', background: view === v ? '#fff' : 'transparent', color: BROWN, border: 0, borderRadius: 4, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700 }}>
                                            {v === 'list' ? '☰ List' : v === 'board' ? '⊞ Board' : '📅 ' + t('Kalendar', 'Calendar')}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {loading ? <p className="muted">{t('Učitavam…', 'Loading…')}</p>
                                : view === 'list' ? (
                                    <ListView sections={sections} tasks={tasks} project={activeProject} onOpen={(id) => setOpenTaskId(id)} onComplete={completeTask}
                                        onCreate={newTaskInline} onMove={moveTask} userById={userById} locale={locale} reload={() => loadProjectTasks(activeProject)} token={token} />
                                ) : view === 'board' ? (
                                    <BoardView sections={sections} tasks={tasks} project={activeProject} onOpen={(id) => setOpenTaskId(id)} onComplete={completeTask}
                                        onCreate={newTaskInline} onMove={moveTask} locale={locale} reload={() => loadProjectTasks(activeProject)} token={token} />
                                ) : (
                                    <CalendarView rows={projectTasksToCal(tasks, activeProject)} month={calMonth} setMonth={setCalMonth} onOpen={(id) => setOpenTaskId(id)} locale={locale} />
                                )}
                        </>
                    )}
                </main>
            </div>

            {showIntake ? (
                <IntakeModal onClose={() => setShowIntake(false)} token={token} locale={locale} isMobile={isMobile}
                    onSent={async () => { await loadDcAll(); }} />
            ) : null}
            {showNewProject ? (
                <NewProjectModal onClose={() => setShowNewProject(false)} onCreated={async (id) => { setShowNewProject(false); await loadProjects(); const p = (await api<{ projects: Project[] }>('/tasks/projects', token!)).projects.find((x) => x.id === id); if (p) setActiveProject(p); }} token={token} locale={locale} />
            ) : null}
            {openTaskId !== null ? (
                <TaskDrawer taskId={openTaskId} onClose={() => setOpenTaskId(null)} token={token} state={state} userById={userById}
                    onChanged={() => { if (activeProject) void loadProjectTasks(activeProject); void loadMyTasks(); }}
                    locale={locale} sections={sections} />
            ) : null}
        </section>
    );
}

function projectTasksToCal(tasks: Task[], p: Project): CalRow[] {
    return tasks.filter((x) => x.due_date).map((x) => ({
        id: x.id, title: x.title, priority: x.priority, due_date: x.due_date!,
        completed_at: x.completed_at, project_id: p.id, project_name: p.name,
        project_color: p.color, project_icon: p.icon, assignee_name: x.assignee_name,
    }));
}

/** ── My Tasks panel ── */
function MyTasksPanel({ mt, onOpen, onComplete, locale }: { mt: MyTasksResp | null; onOpen: (id: number) => void; onComplete: (id: number) => void; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    if (!mt) return <p className="muted" style={{ fontSize: '0.78rem' }}>{t('Učitavam…', 'Loading…')}</p>;
    const Block = ({ title, list, color }: { title: string; list: Task[]; color: string }) => (
        <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: '0.7rem', color, textTransform: 'uppercase', fontWeight: 700, margin: '4px 4px 3px' }}>{title} ({list.length})</div>
            {list.map((x) => (
                <button key={x.id} type="button" onClick={() => onOpen(x.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left', padding: '6px 8px', background: 'transparent', border: 0, borderRadius: 5, cursor: 'pointer', fontSize: '0.78rem', color: BROWN }}>
                    <input type="checkbox" checked={false} onChange={(e) => { e.stopPropagation(); onComplete(x.id); }} onClick={(e) => e.stopPropagation()} />
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.title}</span>
                    {x.due_date ? <span style={{ fontSize: '0.66rem', color: '#888' }}>{fmtDate(x.due_date)}</span> : null}
                </button>
            ))}
        </div>
    );
    return (
        <>
            <Block title={t('Probijena', 'Overdue')} list={mt.overdue} color="#b14040" />
            <Block title={t('Danas', 'Today')} list={mt.today} color="var(--ad-acc-3)" />
            <Block title={t('Uskoro', 'Upcoming')} list={mt.upcoming} color="#1e40af" />
            <Block title={t('Bez datuma', 'No date')} list={mt.no_date} color="#888" />
        </>
    );
}

function EmptyState({ onCreate, locale }: { onCreate: () => void; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    return (
        <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>
            <div style={{ fontSize: '4rem' }}>🗂</div>
            <h2 style={{ color: BROWN }}>{t('Dobrodošao u Zadatke', 'Welcome to Tasks')}</h2>
            <p>{t('Odaberi projekt s lijeve strane ili kreni novi.', 'Pick a project on the left or create a new one.')}</p>
            <button type="button" onClick={onCreate} style={{ background: '#1f7a36', color: '#fff', border: 0, padding: '8px 18px', borderRadius: 6, cursor: 'pointer', fontWeight: 700 }}>+ {t('Novi projekt', 'New project')}</button>
        </div>
    );
}

function SearchResults({ rows, onOpen, locale }: { rows: any[]; onOpen: (id: number) => void; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    if (rows.length === 0) return <p className="muted">{t('Nema rezultata.', 'No results.')}</p>;
    return (
        <div>
            <h3 style={{ color: BROWN }}>🔍 {t('Rezultati pretrage', 'Search results')} ({rows.length})</h3>
            {rows.map((r) => (
                <button key={r.id} type="button" onClick={() => onOpen(r.id)} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', border: '1px solid rgba(var(--ad-soft-rgb),0.2)', borderRadius: 6, background: '#fffdf6', cursor: 'pointer', marginBottom: 5 }}>
                    <span style={{ fontSize: '0.86rem', fontWeight: 600 }}>{r.title}</span>
                    <div style={{ fontSize: '0.7rem', color: '#888' }}>{r.project_icon} {r.project_name} · {r.due_date ? fmtDate(r.due_date) : '—'}</div>
                </button>
            ))}
        </div>
    );
}

/** ── List view ── */
function ListView({ sections, tasks, project, onOpen, onComplete, onCreate, locale, token, reload }: {
    sections: Section[]; tasks: Task[]; project: Project; onOpen: (id: number) => void; onComplete: (id: number) => void;
    onCreate: (projectId: number, sectionId: number | null, title: string) => Promise<void>;
    onMove: (taskId: number, toSectionId: number | null, toOrder?: number) => Promise<void>;
    userById: Record<number, Member>; locale: Locale; reload: () => void; token: string | null;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [newSecName, setNewSecName] = useState('');
    const [newTitleBySec, setNewTitleBySec] = useState<Record<string, string>>({});
    const grouped: Record<string, Task[]> = {};
    sections.forEach((s) => { grouped[String(s.id)] = []; });
    grouped['__null'] = [];
    tasks.forEach((t) => { grouped[String(t.section_id ?? '__null')] ??= []; grouped[String(t.section_id ?? '__null')].push(t); });
    const addSection = async () => {
        if (!token || !newSecName.trim()) return;
        await mutate(`/tasks/projects/${project.id}/sections`, token, 'POST', { name: newSecName.trim() });
        setNewSecName(''); reload();
    };
    return (
        <div>
            {[...sections, { id: -1, name: t('(bez sekcije)', '(no section)'), project_id: project.id, sort_order: 9999 } as Section].map((sec) => {
                const list = grouped[String(sec.id === -1 ? '__null' : sec.id)] ?? [];
                if (sec.id === -1 && list.length === 0) return null;
                const key = String(sec.id);
                return (
                    <div key={sec.id} style={{ marginBottom: 18 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', borderBottom: '1px solid #d9c98a', marginBottom: 4 }}>
                            <strong style={{ color: BROWN }}>{sec.name}</strong>
                            <span className="muted" style={{ fontSize: '0.72rem' }}>{list.length}</span>
                        </div>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem' }}>
                            <thead>
                                <tr style={{ background: 'rgba(var(--ad-acc-rgb),0.05)', color: '#888' }}>
                                    <th style={{ padding: '4px 8px', textAlign: 'left', width: 24 }}></th>
                                    <th style={{ padding: '4px 8px', textAlign: 'left' }}>{t('Zadatak', 'Task')}</th>
                                    <th style={{ padding: '4px 8px', textAlign: 'left', width: 130 }}>{t('Dodijeljeno', 'Assignee')}</th>
                                    <th style={{ padding: '4px 8px', textAlign: 'left', width: 110 }}>{t('Rok', 'Due')}</th>
                                    <th style={{ padding: '4px 8px', textAlign: 'left', width: 90 }}>{t('Prioritet', 'Priority')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {list.map((tt) => {
                                    const p = PRIORITY_META[tt.priority] ?? PRIORITY_META.normal;
                                    return (
                                        <tr key={tt.id} onClick={() => onOpen(tt.id)} style={{ borderBottom: '1px solid #f0ead7', cursor: 'pointer', opacity: tt.completed_at ? 0.5 : 1 }}>
                                            <td style={{ padding: '5px 8px' }} onClick={(e) => e.stopPropagation()}>
                                                <input type="checkbox" checked={!!tt.completed_at} onChange={() => onComplete(tt.id)} />
                                            </td>
                                            <td style={{ padding: '5px 8px', textDecoration: tt.completed_at ? 'line-through' : 'none' }}>
                                                {tt.title}
                                                {(tt.subtask_count ?? 0) > 0 ? <span style={{ marginLeft: 6, fontSize: '0.7rem', color: '#888' }}>· {tt.subtask_count} sub</span> : null}
                                                {(tt.comment_count ?? 0) > 0 ? <span style={{ marginLeft: 6, fontSize: '0.7rem', color: '#888' }}>💬 {tt.comment_count}</span> : null}
                                            </td>
                                            <td style={{ padding: '5px 8px', color: '#666' }}>{tt.assignee_name ?? '—'}</td>
                                            <td style={{ padding: '5px 8px', color: tt.due_date && tt.due_date < todayIso() && !tt.completed_at ? '#b14040' : '#666' }}>{tt.due_date ? fmtDate(tt.due_date) : '—'}</td>
                                            <td style={{ padding: '5px 8px' }}>
                                                <span style={{ color: p.color, fontWeight: 700 }}>{p.icon} {locale === 'en' ? p.en : p.hr}</span>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {sec.id !== -1 ? (
                                    <tr>
                                        <td colSpan={5} style={{ padding: '5px 8px' }}>
                                            <input type="text" placeholder={t('+ Dodaj zadatak…', '+ Add task…')} value={newTitleBySec[key] ?? ''}
                                                onChange={(e) => setNewTitleBySec((v) => ({ ...v, [key]: e.target.value }))}
                                                onKeyDown={async (e) => { if (e.key === 'Enter') { await onCreate(project.id, sec.id, newTitleBySec[key] ?? ''); setNewTitleBySec((v) => ({ ...v, [key]: '' })); } }}
                                                style={{ width: '100%', padding: '4px 8px', border: '1px dashed rgba(var(--ad-soft-rgb),0.4)', borderRadius: 4, background: 'transparent', fontSize: '0.84rem', color: BROWN }} />
                                        </td>
                                    </tr>
                                ) : null}
                            </tbody>
                        </table>
                    </div>
                );
            })}
            <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
                <input type="text" placeholder={t('+ Nova sekcija', '+ New section')} value={newSecName} onChange={(e) => setNewSecName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') void addSection(); }}
                    style={{ padding: '5px 10px', border: '1px dashed var(--ad-acc)', borderRadius: 5, fontSize: '0.82rem', flex: 1 }} />
            </div>
        </div>
    );
}

/** ── Board (Kanban) view ── */
function BoardView({ sections, tasks, project, onOpen, onComplete, onCreate, onMove, locale, reload, token }: {
    sections: Section[]; tasks: Task[]; project: Project; onOpen: (id: number) => void; onComplete: (id: number) => void;
    onCreate: (projectId: number, sectionId: number | null, title: string) => Promise<void>;
    onMove: (taskId: number, toSectionId: number | null, toOrder?: number) => Promise<void>;
    locale: Locale; reload: () => void; token: string | null;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [dragId, setDragId] = useState<number | null>(null);
    const grouped: Record<string, Task[]> = {};
    sections.forEach((s) => { grouped[String(s.id)] = []; });
    tasks.forEach((tt) => { const k = String(tt.section_id ?? sections[0]?.id ?? ''); grouped[k] ??= []; grouped[k].push(tt); });
    const [newTitleBySec, setNewTitleBySec] = useState<Record<string, string>>({});
    return (
        <div style={{ display: 'flex', gap: 14, overflowX: 'auto', paddingBottom: 12 }}>
            {sections.map((sec) => {
                const list = grouped[String(sec.id)] ?? [];
                return (
                    <div key={sec.id} style={{ minWidth: 280, maxWidth: 320, flex: '0 0 300px', background: 'rgba(var(--ad-acc-rgb),0.05)', border: '1px solid #d9c98a', borderRadius: 8, padding: 8 }}
                        onDragOver={(e) => { e.preventDefault(); }}
                        onDrop={async () => { if (dragId) { await onMove(dragId, sec.id); setDragId(null); } }}>
                        <div style={{ fontWeight: 700, color: BROWN, padding: '4px 6px', marginBottom: 6 }}>{sec.name} <span className="muted" style={{ fontSize: '0.72rem', fontWeight: 400 }}>({list.length})</span></div>
                        {list.map((tt) => {
                            const p = PRIORITY_META[tt.priority] ?? PRIORITY_META.normal;
                            return (
                                <div key={tt.id} draggable onDragStart={() => setDragId(tt.id)} onClick={() => onOpen(tt.id)}
                                    style={{ background: '#fffdf6', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderLeft: `3px solid ${p.color}`, borderRadius: 6, padding: '8px 10px', marginBottom: 6, cursor: 'pointer', opacity: tt.completed_at ? 0.5 : 1 }}>
                                    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                                        <input type="checkbox" checked={!!tt.completed_at} onChange={(e) => { e.stopPropagation(); onComplete(tt.id); }} onClick={(e) => e.stopPropagation()} />
                                        <div style={{ flex: 1, fontSize: '0.84rem', textDecoration: tt.completed_at ? 'line-through' : 'none' }}>{tt.title}</div>
                                        {tt.asana_gid ? <span title="Asana" style={{ fontSize: '0.72rem' }}>🟧</span> : null}
                                    </div>
                                    <div style={{ display: 'flex', gap: 8, marginTop: 5, fontSize: '0.72rem', color: '#888' }}>
                                        {tt.assignee_name ? <span>👤 {tt.assignee_name}</span> : null}
                                        {tt.due_date ? <span style={{ color: tt.due_date < todayIso() && !tt.completed_at ? '#b14040' : '#888' }}>📅 {fmtDate(tt.due_date)}</span> : null}
                                        {(tt.subtask_count ?? 0) > 0 ? <span>· {tt.subtask_count} sub</span> : null}
                                        {(tt.comment_count ?? 0) > 0 ? <span>💬 {tt.comment_count}</span> : null}
                                        {tt.asana_permalink_url ? <a href={tt.asana_permalink_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ marginLeft: 'auto', color: '#f06a35', fontSize: '0.7rem', textDecoration: 'underline' }}>{t('otvori u Asani →', 'open in Asana →')}</a> : null}
                                    </div>
                                </div>
                            );
                        })}
                        <input type="text" placeholder={t('+ Dodaj…', '+ Add…')} value={newTitleBySec[String(sec.id)] ?? ''}
                            onChange={(e) => setNewTitleBySec((v) => ({ ...v, [String(sec.id)]: e.target.value }))}
                            onKeyDown={async (e) => { if (e.key === 'Enter') { await onCreate(project.id, sec.id, newTitleBySec[String(sec.id)] ?? ''); setNewTitleBySec((v) => ({ ...v, [String(sec.id)]: '' })); } }}
                            style={{ width: '100%', padding: '5px 8px', border: '1px dashed rgba(var(--ad-soft-rgb),0.4)', borderRadius: 4, background: 'transparent', fontSize: '0.78rem' }} />
                    </div>
                );
            })}
        </div>
    );
}

/** ── Calendar view (month grid) ── */
function CalendarView({ rows, month, setMonth, onOpen, locale }: { rows: CalRow[]; month: Date; setMonth: (d: Date) => void; onOpen: (id: number) => void; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const y = month.getFullYear(); const m = month.getMonth();
    const first = new Date(y, m, 1);
    const firstWeekday = (first.getDay() + 6) % 7; // start Mon
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const dayCells: Array<{ day: number | null; iso?: string; items?: CalRow[] }> = [];
    for (let i = 0; i < firstWeekday; i++) dayCells.push({ day: null });
    const byDate: Record<string, CalRow[]> = {};
    rows.forEach((r) => { (byDate[r.due_date] ??= []).push(r); });
    for (let d = 1; d <= daysInMonth; d++) {
        const iso = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        dayCells.push({ day: d, iso, items: byDate[iso] ?? [] });
    }
    const monthLabel = month.toLocaleDateString(locale === 'en' ? 'en-GB' : 'hr-HR', { month: 'long', year: 'numeric' });
    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <button type="button" onClick={() => setMonth(new Date(y, m - 1, 1))} style={{ padding: '4px 10px', background: 'transparent', border: '1px solid var(--ad-acc)', borderRadius: 5, cursor: 'pointer' }}>‹</button>
                <h2 style={{ margin: 0, color: BROWN, textTransform: 'capitalize' }}>{monthLabel}</h2>
                <button type="button" onClick={() => setMonth(new Date(y, m + 1, 1))} style={{ padding: '4px 10px', background: 'transparent', border: '1px solid var(--ad-acc)', borderRadius: 5, cursor: 'pointer' }}>›</button>
                <button type="button" onClick={() => setMonth(new Date())} style={{ marginLeft: 'auto', padding: '4px 10px', background: GOLD, color: '#fff', border: 0, borderRadius: 5, cursor: 'pointer', fontSize: '0.78rem' }}>{t('Danas', 'Today')}</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, background: '#d9c98a' }}>
                {[t('Pon', 'Mon'), t('Uto', 'Tue'), t('Sri', 'Wed'), t('Čet', 'Thu'), t('Pet', 'Fri'), t('Sub', 'Sat'), t('Ned', 'Sun')].map((d, i) => (
                    <div key={i} style={{ padding: '6px 8px', background: '#fffdf6', fontWeight: 700, color: BROWN, fontSize: '0.78rem' }}>{d}</div>
                ))}
                {dayCells.map((c, i) => (
                    <div key={i} style={{ minHeight: 90, background: c.day ? '#fffdf6' : '#fcf6e3', padding: 4 }}>
                        {c.day ? <div style={{ fontSize: '0.74rem', color: c.iso === todayIso() ? '#b14040' : '#888', fontWeight: c.iso === todayIso() ? 700 : 400 }}>{c.day}</div> : null}
                        {(c.items ?? []).slice(0, 4).map((it) => (
                            <button key={it.id} type="button" onClick={() => onOpen(it.id)}
                                style={{ display: 'block', width: '100%', textAlign: 'left', background: it.project_color, color: '#fff', borderRadius: 3, padding: '2px 5px', margin: '2px 0', fontSize: '0.7rem', border: 0, cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: it.completed_at ? 0.55 : 1, textDecoration: it.completed_at ? 'line-through' : 'none' }}>
                                {it.project_icon} {it.title}
                            </button>
                        ))}
                        {(c.items?.length ?? 0) > 4 ? <div style={{ fontSize: '0.7rem', color: '#888' }}>+{(c.items!.length - 4)}</div> : null}
                    </div>
                ))}
            </div>
        </div>
    );
}

/** ── New Project modal ── */
function NewProjectModal({ onClose, onCreated, token, locale }: { onClose: () => void; onCreated: (id: number) => void; token: string | null; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [name, setName] = useState('');
    const [icon, setIcon] = useState('📋');
    const [color, setColor] = useState('var(--ad-acc)');
    const [viewDefault, setViewDefault] = useState<View>('list');
    const [busy, setBusy] = useState(false);
    const save = async () => {
        if (!token || !name.trim()) return;
        setBusy(true);
        try {
            const r = await mutate<{ id: number }>('/tasks/projects', token, 'POST', { name, icon, color, view_default: viewDefault });
            onCreated(r.id);
        } catch (e) { alert(String(e)); } finally { setBusy(false); }
    };
    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: '#fffdf6', padding: 18, borderRadius: 8, width: 'min(420px, 96vw)', boxShadow: '0 8px 30px rgba(0,0,0,0.3)' }}>
                <h3 style={{ margin: 0, marginBottom: 10, color: BROWN }}>+ {t('Novi projekt', 'New project')}</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: 8, fontSize: '0.84rem' }}>
                    <label>{t('Naziv', 'Name')}</label>
                    <input autoFocus type="text" value={name} onChange={(e) => setName(e.target.value)} style={{ padding: '4px 6px' }} />
                    <label>{t('Ikona', 'Icon')}</label>
                    <input type="text" value={icon} onChange={(e) => setIcon(e.target.value)} placeholder="📋" maxLength={4} style={{ padding: '4px 6px', width: 60, fontSize: '1.1rem' }} />
                    <label>{t('Boja', 'Color')}</label>
                    <input type="color" value={color} onChange={(e) => setColor(e.target.value)} style={{ width: 60, padding: 0, border: 0 }} />
                    <label>{t('Default view', 'Default view')}</label>
                    <select value={viewDefault} onChange={(e) => setViewDefault(e.target.value as View)}>
                        <option value="list">List</option><option value="board">Board</option><option value="calendar">Calendar</option>
                    </select>
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
                    <button type="button" onClick={onClose} style={{ background: 'transparent', border: '1px solid #999', color: '#666', padding: '5px 12px', borderRadius: 4, cursor: 'pointer' }}>{t('Odustani', 'Cancel')}</button>
                    <button type="button" disabled={busy || !name.trim()} onClick={() => void save()} style={{ background: '#1f7a36', color: '#fff', border: 0, padding: '5px 12px', borderRadius: 4, cursor: 'pointer', fontWeight: 700 }}>{busy ? '…' : t('Kreiraj', 'Create')}</button>
                </div>
            </div>
        </div>
    );
}

/** ── Task drawer (Asana-stil pravoslo) ── */
function TaskDrawer({ taskId, onClose, token, state, userById, onChanged, locale, sections }: {
    taskId: number; onClose: () => void; token: string | null; state: StateResp | null;
    userById: Record<number, Member>; onChanged: () => void; locale: Locale; sections: Section[];
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<any>(null);
    const [newComment, setNewComment] = useState('');
    const [newSub, setNewSub] = useState('');
    const reload = useCallback(async () => {
        if (!token) return;
        try { setData(await api<any>(`/tasks/tasks/${taskId}`, token)); } catch (e) { alert(String(e)); }
    }, [taskId, token]);
    useEffect(() => { void reload(); }, [taskId]);
    if (!data) return null;
    const task = data.task;
    const patch = async (body: any) => {
        if (!token) return;
        await mutate(`/tasks/tasks/${taskId}`, token, 'PATCH', body);
        await reload(); onChanged();
    };
    const complete = async () => {
        if (!token) return;
        await mutate(`/tasks/tasks/${taskId}/complete`, token, 'POST', {});
        await reload(); onChanged();
    };
    const addComment = async () => {
        if (!token || !newComment.trim()) return;
        await mutate(`/tasks/tasks/${taskId}/comments`, token, 'POST', { body: newComment.trim() });
        setNewComment(''); await reload();
    };
    const addSubtask = async () => {
        if (!token || !newSub.trim()) return;
        await mutate('/tasks/tasks', token, 'POST', { project_id: task.project_id, parent_task_id: taskId, title: newSub.trim() });
        setNewSub(''); await reload(); onChanged();
    };
    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 100, display: 'flex', justifyContent: 'flex-end' }}>
            <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(620px, 96vw)', background: '#fffdf6', height: '100%', overflow: 'auto', padding: 20, boxShadow: '-8px 0 30px rgba(0,0,0,0.3)' }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
                    <button type="button" onClick={complete} style={{ background: task.completed_at ? '#1f7a36' : 'transparent', color: task.completed_at ? '#fff' : '#1f7a36', border: '1px solid #1f7a36', padding: '4px 10px', borderRadius: 5, cursor: 'pointer', fontWeight: 700 }}>{task.completed_at ? '✓ ' + t('Obavljeno', 'Done') : '○ ' + t('Označi gotovim', 'Mark complete')}</button>
                    <div style={{ flex: 1 }} />
                    <button type="button" onClick={onClose} style={{ background: 'transparent', border: 0, fontSize: '1.4rem', cursor: 'pointer' }}>✕</button>
                </div>
                <input type="text" defaultValue={task.title} onBlur={(e) => { if (e.target.value !== task.title) void patch({ title: e.target.value }); }}
                    style={{ width: '100%', fontSize: '1.3rem', fontWeight: 700, color: BROWN, border: 0, background: 'transparent', padding: '6px 0', marginBottom: 10 }} />
                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8, fontSize: '0.84rem', marginBottom: 16 }}>
                    <label style={{ color: '#888', paddingTop: 4 }}>{t('Dodijeljeno', 'Assignee')}</label>
                    <select value={task.assignee_user_id ?? ''} onChange={(e) => void patch({ assignee_user_id: e.target.value ? Number(e.target.value) : null })} style={{ padding: '4px 6px' }}>
                        <option value="">— {t('Nitko', 'Nobody')} —</option>
                        {Object.values(userById).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                    <label style={{ color: '#888', paddingTop: 4 }}>{t('Rok', 'Due')}</label>
                    <DateInput value={task.due_date ?? ''} onChange={(v) => void patch({ due_date: v || null })} />
                    <label style={{ color: '#888', paddingTop: 4 }}>{t('Prioritet', 'Priority')}</label>
                    <select value={task.priority} onChange={(e) => void patch({ priority: e.target.value })} style={{ padding: '4px 6px' }}>
                        {Object.entries(PRIORITY_META).map(([k, m]) => <option key={k} value={k}>{m.icon} {locale === 'en' ? m.en : m.hr}</option>)}
                    </select>
                    <label style={{ color: '#888', paddingTop: 4 }}>{t('Sekcija', 'Section')}</label>
                    <select value={task.section_id ?? ''} onChange={(e) => void patch({ section_id: e.target.value ? Number(e.target.value) : null })} style={{ padding: '4px 6px' }}>
                        <option value="">—</option>
                        {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                </div>
                <div style={{ marginBottom: 16 }}>
                    <label style={{ fontSize: '0.74rem', color: '#888', fontWeight: 700, textTransform: 'uppercase' }}>{t('Opis', 'Description')}</label>
                    <textarea defaultValue={task.description ?? ''} onBlur={(e) => { if (e.target.value !== (task.description ?? '')) void patch({ description: e.target.value }); }}
                        placeholder={t('Što treba odraditi?', 'What needs to be done?')} rows={4}
                        style={{ width: '100%', padding: '8px 10px', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 5, fontSize: '0.86rem' }} />
                </div>
                <div style={{ marginBottom: 16 }}>
                    <label style={{ fontSize: '0.74rem', color: '#888', fontWeight: 700, textTransform: 'uppercase' }}>{t('Pod-zadaci', 'Subtasks')} ({(data.subtasks ?? []).length})</label>
                    {(data.subtasks ?? []).map((s: any) => (
                        <div key={s.id} style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '4px 0' }}>
                            <input type="checkbox" checked={!!s.completed_at} readOnly />
                            <span style={{ fontSize: '0.84rem', textDecoration: s.completed_at ? 'line-through' : 'none', opacity: s.completed_at ? 0.5 : 1 }}>{s.title}</span>
                            {s.assignee_name ? <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: '#888' }}>{s.assignee_name}</span> : null}
                        </div>
                    ))}
                    <input type="text" placeholder={t('+ Dodaj pod-zadatak…', '+ Add subtask…')} value={newSub} onChange={(e) => setNewSub(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') void addSubtask(); }}
                        style={{ width: '100%', padding: '4px 8px', border: '1px dashed rgba(var(--ad-soft-rgb),0.4)', borderRadius: 4, fontSize: '0.84rem', marginTop: 4 }} />
                </div>
                <div style={{ marginBottom: 16 }}>
                    <label style={{ fontSize: '0.74rem', color: '#888', fontWeight: 700, textTransform: 'uppercase' }}>💬 {t('Komentari', 'Comments')} ({(data.comments ?? []).length})</label>
                    {(data.comments ?? []).map((c: any) => (
                        <div key={c.id} style={{ background: 'var(--ad-bg)', border: '1px solid rgba(var(--ad-soft-rgb),0.2)', borderRadius: 5, padding: '7px 10px', marginBottom: 5 }}>
                            <div style={{ display: 'flex', gap: 6, fontSize: '0.74rem', color: '#666', marginBottom: 3 }}>
                                <strong style={{ color: BROWN }}>{c.author_name ?? c.author_email}</strong>
                                <span>· {new Date(c.created_at).toLocaleString(locale === 'en' ? 'en-GB' : 'hr-HR')}</span>
                            </div>
                            <div style={{ fontSize: '0.86rem', whiteSpace: 'pre-wrap' }}>{c.body}</div>
                        </div>
                    ))}
                    <textarea value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder={t('Napiši komentar…', 'Write a comment…')} rows={2}
                        style={{ width: '100%', padding: '6px 8px', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 5, fontSize: '0.84rem', marginTop: 4 }} />
                    <button type="button" onClick={() => void addComment()} disabled={!newComment.trim()} style={{ background: GOLD, color: '#fff', border: 0, padding: '4px 12px', borderRadius: 4, cursor: 'pointer', marginTop: 4, fontWeight: 700 }}>{t('Pošalji', 'Send')}</button>
                </div>
                {(data.activity ?? []).length > 0 ? (
                    <div>
                        <label style={{ fontSize: '0.74rem', color: '#888', fontWeight: 700, textTransform: 'uppercase' }}>{t('Aktivnost', 'Activity')}</label>
                        {(data.activity ?? []).slice(0, 10).map((a: any) => (
                            <div key={a.id} style={{ fontSize: '0.74rem', color: '#888', padding: '2px 0' }}>
                                {a.actor_name ?? 'Sustav'} · <strong>{a.action}</strong> · {new Date(a.created_at).toLocaleString(locale === 'en' ? 'en-GB' : 'hr-HR')}
                            </div>
                        ))}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

/** ── DeChaos sidebar ── */
function DcSidebar({ health, projects, active, view, onView, onProject, onRefresh, loading, locale, onWrapNav, triagePending }: {
    health: DcHealth | null; projects: DcProject[]; active: DcProject | null; view: string;
    onView: (v: 'wrap' | 'today2' | 'projects' | 'waiting' | 'triage' | 'settings') => void; onProject: (p: DcProject) => void;
    onRefresh: () => void; loading: boolean; locale: Locale; onWrapNav?: (path: string) => void;
    triagePending?: number;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    if (!health) return <p className="muted" style={{ fontSize: '0.78rem' }}>{t('Učitavam DeChaos…', 'Loading DeChaos…')}</p>;
    if (!health.configured) return (
        <div style={{ background: 'rgba(177,64,64,0.08)', border: '1px solid #b14040', borderRadius: 5, padding: 8, fontSize: '0.78rem', color: '#8a3a3a' }}>
            <strong>⚠ DeChaos nije konfiguriran</strong><br />
            Postavi <code>DECHAOS_URL</code> + <code>DECHAOS_TOKEN</code> u <code>.env</code>.<br />
            {health.error}
        </div>
    );
    if (!health.reachable) return (
        <div style={{ background: 'rgba(177,64,64,0.08)', border: '1px solid #b14040', borderRadius: 5, padding: 8, fontSize: '0.78rem', color: '#8a3a3a' }}>
            <strong>⚠ DeChaos nije dostupan</strong><br />
            {health.error}<br />
            <button type="button" onClick={onRefresh} style={{ marginTop: 4, background: '#b14040', color: '#fff', border: 0, padding: '3px 8px', borderRadius: 4, cursor: 'pointer' }}>↻ {t('Pokušaj ponovo', 'Retry')}</button>
        </div>
    );
    const nav = (path: string) => () => { onView('wrap'); onWrapNav?.(path); };
    return (
        <>
            <div style={{ fontSize: '0.7rem', color: '#888', marginBottom: 8 }}>{t('Spojen kao', 'Connected as')}: <strong>{health.as_user}</strong> {loading ? '· ⏳' : null}</div>
            <button type="button" onClick={() => onView('triage')}
                style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left', padding: '8px 10px', background: view === 'triage' ? '#b14040' : (triagePending && triagePending > 0 ? 'rgba(177,64,64,0.13)' : 'transparent'), color: view === 'triage' ? '#fff' : '#b14040', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.92rem', marginBottom: 4, fontWeight: 800 }}>
                🔥 {t('Triaža', 'Triage')}
                {triagePending && triagePending > 0 ? <span style={{ marginLeft: 'auto', background: view === 'triage' ? '#fff' : '#b14040', color: view === 'triage' ? '#b14040' : '#fff', borderRadius: 10, padding: '1px 8px', fontSize: '0.74rem', fontWeight: 800 }}>{triagePending}</span> : null}
            </button>
            <button type="button" onClick={() => onView('settings')}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', background: view === 'settings' ? 'rgba(var(--ad-soft-rgb),0.18)' : 'transparent', border: 0, borderRadius: 6, cursor: 'pointer', fontSize: '0.84rem', marginBottom: 6, color: BROWN, fontWeight: view === 'settings' ? 700 : 400 }}>⚙ {t('Postavke', 'Settings')}</button>
            <div style={{ fontSize: '0.66rem', color: '#888', textTransform: 'uppercase', margin: '6px 4px 4px', letterSpacing: 0.5 }}>{t('PUNI DECHAOS', 'FULL DECHAOS')}</div>
            <button type="button" onClick={nav('/dashboard')}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', background: view === 'wrap' ? 'rgba(177,64,64,0.13)' : 'transparent', border: 0, borderRadius: 6, cursor: 'pointer', fontSize: '0.86rem', marginBottom: 2, color: '#8a3a3a', fontWeight: view === 'wrap' ? 700 : 400 }}>🏠 Dashboard</button>
            <button type="button" onClick={nav('/today3')}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', background: 'transparent', border: 0, borderRadius: 6, cursor: 'pointer', fontSize: '0.86rem', marginBottom: 2, color: '#8a3a3a' }}>🎯 Today3</button>
            <button type="button" onClick={nav('/today2')}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', background: 'transparent', border: 0, borderRadius: 6, cursor: 'pointer', fontSize: '0.86rem', marginBottom: 2, color: '#8a3a3a' }}>📋 Today2</button>
            <button type="button" onClick={nav('/nova2/intakes/triage')}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', background: 'transparent', border: 0, borderRadius: 6, cursor: 'pointer', fontSize: '0.86rem', marginBottom: 2, color: '#b14040', fontWeight: 700 }}>🔥 {t('Triaža', 'Triage')}</button>
            <button type="button" onClick={nav('/weekly')}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', background: 'transparent', border: 0, borderRadius: 6, cursor: 'pointer', fontSize: '0.86rem', marginBottom: 2, color: '#8a3a3a' }}>📅 Weekly</button>
            <button type="button" onClick={nav('/timeblock')}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', background: 'transparent', border: 0, borderRadius: 6, cursor: 'pointer', fontSize: '0.86rem', marginBottom: 2, color: '#8a3a3a' }}>🧱 Timeblock</button>
            <div style={{ fontSize: '0.66rem', color: '#888', textTransform: 'uppercase', margin: '12px 4px 4px', letterSpacing: 0.5 }}>{t('NATIV (kompakt)', 'NATIVE (compact)')}</div>
            <button type="button" onClick={() => onView('today2')}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', background: view === 'today2' ? 'rgba(177,64,64,0.13)' : 'transparent', border: 0, borderRadius: 6, cursor: 'pointer', fontSize: '0.84rem', marginBottom: 3, color: '#8a3a3a', fontWeight: view === 'today2' ? 700 : 400 }}>🎯 {t('Today2 kompakt', 'Today2 compact')}</button>
            <button type="button" onClick={() => onView('waiting')}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', background: view === 'waiting' ? 'rgba(var(--ad-soft-rgb),0.18)' : 'transparent', border: 0, borderRadius: 6, cursor: 'pointer', fontSize: '0.84rem', marginBottom: 3, color: 'var(--ad-ink)', fontWeight: view === 'waiting' ? 700 : 400 }}>⏸ {t('Čeka odgovor', 'Waiting on others')}</button>
            <div style={{ fontSize: '0.66rem', color: '#888', textTransform: 'uppercase', margin: '10px 4px 4px', letterSpacing: 0.5 }}>{t('Projekti (kompakt)', 'Projects (compact)')}</div>
            {projects.length === 0 ? <p className="muted" style={{ fontSize: '0.78rem' }}>{t('Nema projekata', 'No projects')}</p> : null}
            {projects.map((p) => (
                <button key={p.id} type="button" onClick={() => onProject(p)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left', padding: '7px 8px', background: active?.id === p.id ? 'rgba(177,64,64,0.13)' : 'transparent', border: 0, borderRadius: 6, cursor: 'pointer', fontSize: '0.86rem', marginBottom: 2, color: BROWN }}>
                    <span>📁</span>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title}</span>
                    <span style={{ fontSize: '0.66rem', color: '#888' }}>{p.open_count}/{p.task_count}</span>
                </button>
            ))}
            <button type="button" onClick={onRefresh} style={{ marginTop: 10, width: '100%', background: 'transparent', color: '#8a3a3a', border: '1px dashed #b14040', padding: '4px 8px', borderRadius: 5, cursor: 'pointer', fontSize: '0.76rem' }}>↻ {t('Osvježi', 'Refresh')}</button>
        </>
    );
}

function DcMain({ health, view, today, activeProject, projectTasks, onComplete, locale, loading, wrapUrl, wrapBusy, wrapUser, onReloadWrap, onNavWrap, triage, triageBusy, onApprove, onDiscard, onReloadTriage, settings, settingsDraft, onSettingsDraft, onSaveSettings }: {
    health: DcHealth | null; view: string; today: DcToday | null;
    activeProject: DcProject | null; projectTasks: DcTask[];
    onComplete: (id: number, reopen?: boolean) => void;
    locale: Locale; loading: boolean;
    wrapUrl: string | null; wrapBusy: boolean; wrapUser: { email: string; name: string; was_created: boolean } | null;
    onReloadWrap: () => void; onNavWrap?: (path: string) => void;
    triage?: { count: number; rows: any[] } | null; triageBusy?: number | null;
    onApprove?: (id: number) => void; onDiscard?: (id: number) => void; onReloadTriage?: () => void;
    settings?: any; settingsDraft?: any; onSettingsDraft?: (d: any) => void; onSaveSettings?: () => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    if (!health || !health.configured || !health.reachable) return <p className="muted">{t('DeChaos nije dostupan', 'DeChaos unavailable')}</p>;

    if (view === 'triage') {
        const rows = triage?.rows ?? [];
        return (
            <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <h2 style={{ margin: 0, color: '#b14040' }}>🔥 {t('Triaža', 'Triage')}</h2>
                    <span style={{ background: '#b14040', color: '#fff', borderRadius: 12, padding: '2px 10px', fontWeight: 800 }}>{triage?.count ?? 0}</span>
                    <button type="button" onClick={onReloadTriage} style={{ marginLeft: 'auto', background: 'transparent', border: '1px solid var(--ad-acc)', color: BROWN, padding: '3px 10px', borderRadius: 5, cursor: 'pointer', fontSize: '0.78rem' }}>↻ {t('Osvježi', 'Reload')}</button>
                </div>
                {rows.length === 0 ? <p className="muted" style={{ color: '#1f7a36' }}>✓ {t('Triaža je čista — sve obrađeno.', 'Triage is clear — all processed.')}</p> : null}
                {rows.map((r: any) => (
                    <div key={r.id} style={{ background: 'var(--ad-bg)', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 7, padding: '12px 14px', marginBottom: 10 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                            <strong style={{ fontSize: '0.96rem', color: BROWN }}>{r.title}</strong>
                            <span style={{ background: 'rgba(177,64,64,0.12)', color: '#b14040', borderRadius: 4, padding: '1px 7px', fontSize: '0.7rem', fontWeight: 700 }}>{r.source_type}</span>
                            <span style={{ fontSize: '0.72rem', color: '#888', marginLeft: 'auto' }}>{new Date(r.created_at).toLocaleString(locale === 'en' ? 'en-GB' : 'hr-HR')}</span>
                        </div>
                        {r.ai_canonical_summary ? <div style={{ fontSize: '0.84rem', color: '#444', marginTop: 6, padding: '4px 8px', background: 'rgba(31,122,54,0.06)', borderLeft: '3px solid #1f7a36', borderRadius: 3 }}>📝 {r.ai_canonical_summary}</div> : null}
                        {r.raw_text ? <div style={{ fontSize: '0.78rem', color: '#666', marginTop: 6, whiteSpace: 'pre-wrap', maxHeight: 80, overflow: 'auto' }}>{r.raw_text}</div> : null}
                        {(r.proposed_tasks ?? []).length > 0 ? (
                            <div style={{ marginTop: 8 }}>
                                <div style={{ fontSize: '0.72rem', color: '#888', textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}>{t('AI predlaže', 'AI proposes')}:</div>
                                {(r.proposed_tasks ?? []).map((tsk: any, i: number) => (
                                    <div key={i} style={{ fontSize: '0.84rem', color: '#333', padding: '2px 0' }}>• <strong>{tsk.title}</strong>{tsk.notes ? ' — ' + tsk.notes : ''}{tsk.due_at ? ' · 📅 ' + new Date(tsk.due_at).toLocaleString(locale === 'en' ? 'en-GB' : 'hr-HR') : ''}</div>
                                ))}
                            </div>
                        ) : null}
                        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                            <button type="button" disabled={triageBusy === r.id} onClick={() => onApprove?.(r.id)}
                                style={{ background: '#1f7a36', color: '#fff', border: 0, padding: '6px 14px', borderRadius: 5, cursor: 'pointer', fontWeight: 700, opacity: triageBusy === r.id ? 0.5 : 1 }}>✓ {t('Approve (kreiraj taskove)', 'Approve (create tasks)')}</button>
                            <button type="button" disabled={triageBusy === r.id} onClick={() => onDiscard?.(r.id)}
                                style={{ background: 'transparent', color: '#b14040', border: '1px solid #b14040', padding: '6px 14px', borderRadius: 5, cursor: 'pointer', fontWeight: 700 }}>✕ {t('Discard', 'Discard')}</button>
                        </div>
                    </div>
                ))}
            </div>
        );
    }
    if (view === 'settings') {
        const u = settingsDraft ?? {};
        const inboundEmail = settings?.user?.inbound_email ?? null;
        return (
            <div>
                <h2 style={{ color: BROWN, margin: '4px 0 14px' }}>⚙ {t('DeChaos postavke', 'DeChaos settings')}</h2>
                {!settings ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : (
                    <>
                        <div style={{ background: 'rgba(177,64,64,0.06)', border: '1px solid #b14040', borderRadius: 6, padding: 12, marginBottom: 14 }}>
                            <div style={{ fontSize: '0.74rem', color: '#888', textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}>🌪 {t('Tvoj DeChaos email za intake', 'Your DeChaos intake email')}</div>
                            <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: '1.05rem', color: '#b14040', fontWeight: 700 }}>{inboundEmail ?? '—'}</div>
                            <button type="button" onClick={() => navigator.clipboard?.writeText(inboundEmail ?? '')} style={{ marginTop: 6, background: 'transparent', border: '1px solid var(--ad-acc)', color: BROWN, padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: '0.78rem' }}>📋 {t('Kopiraj', 'Copy')}</button>
                            <div style={{ fontSize: '0.74rem', color: '#666', marginTop: 6 }}>{t('Pošalji mail s prilogom (PDF, slika, tekst) na ovu adresu — DeChaos AI će ga obraditi i predložiti taskove.', 'Send mail with attachment (PDF, image, text) to this address — DeChaos AI processes it and proposes tasks.')}</div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 10, fontSize: '0.86rem', maxWidth: 480 }}>
                            <label>{t('Ime', 'Name')}</label>
                            <input type="text" value={u.name ?? ''} onChange={(e) => onSettingsDraft?.({ ...u, name: e.target.value })} style={{ padding: '6px 8px' }} />
                            <label>{t('Local part emaila', 'Email local part')}</label>
                            <input type="text" value={u.inbound_local_part ?? ''} onChange={(e) => onSettingsDraft?.({ ...u, inbound_local_part: e.target.value })} placeholder="ime.prezime" pattern="[a-z0-9.\-]+" style={{ padding: '6px 8px', fontFamily: 'ui-monospace, monospace' }} />
                            <label>{t('Jezik', 'Locale')}</label>
                            <select value={u.locale ?? 'hr'} onChange={(e) => onSettingsDraft?.({ ...u, locale: e.target.value })} style={{ padding: '6px 8px' }}>
                                <option value="hr">Hrvatski</option><option value="en">English</option>
                            </select>
                            <label>{t('Vremenska zona', 'Timezone')}</label>
                            <input type="text" value={u.timezone ?? ''} onChange={(e) => onSettingsDraft?.({ ...u, timezone: e.target.value })} style={{ padding: '6px 8px' }} />
                        </div>
                        <div style={{ marginTop: 14 }}>
                            <button type="button" onClick={onSaveSettings} style={{ background: '#1f7a36', color: '#fff', border: 0, padding: '7px 16px', borderRadius: 6, cursor: 'pointer', fontWeight: 700 }}>💾 {t('Spremi', 'Save')}</button>
                        </div>
                    </>
                )}
            </div>
        );
    }
    if (view === 'wrap') {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 140px)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0 8px', fontSize: '0.74rem', color: '#888', flexWrap: 'wrap' }}>
                    <span>🌪 {t('Puni DeChaos (SSO)', 'Full DeChaos (SSO)')}</span>
                    {wrapUser ? (
                        <span style={{ background: wrapUser.was_created ? 'rgba(31,122,54,0.12)' : 'rgba(var(--ad-soft-rgb),0.18)', color: wrapUser.was_created ? '#1f7a36' : 'var(--ad-ink)', padding: '1px 7px', borderRadius: 4, fontWeight: 700 }}>
                            👤 {wrapUser.name} {wrapUser.was_created ? '· ✨ ' + t('novi', 'new') : ''}
                        </span>
                    ) : null}
                    <div style={{ flex: 1 }} />
                    <a href={wrapUrl ?? 'https://dechaos.humanitics.eu'} target="_blank" rel="noopener" style={{ color: '#8a3a3a' }}>↗ {t('Otvori u novom tabu', 'Open in new tab')}</a>
                    <button type="button" onClick={onReloadWrap} disabled={wrapBusy} style={{ background: 'transparent', border: '1px solid var(--ad-acc)', color: BROWN, padding: '2px 8px', borderRadius: 4, cursor: 'pointer', fontSize: '0.72rem' }}>{wrapBusy ? '…' : '↻ ' + t('Nova prijava', 'Re-auth')}</button>
                </div>
                {wrapUrl ? (
                    <iframe src={wrapUrl} title="DeChaos" style={{ flex: 1, width: '100%', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 6, background: '#fff' }}
                        sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals allow-downloads" />
                ) : <p className="muted">{t('Učitavam SSO link…', 'Loading SSO link…')}</p>}
            </div>
        );
    }
    if (loading) return <p className="muted">{t('Učitavam DeChaos…', 'Loading DeChaos…')}</p>;

    if (view === 'project_detail' && activeProject) {
        return <DcProjectDetail project={activeProject} tasks={projectTasks} onComplete={onComplete} locale={locale} />;
    }
    if (view === 'waiting') {
        const w = today?.waiting ?? [];
        return (
            <div>
                <h2 style={{ color: BROWN, margin: '4px 0 12px' }}>⏸ {t('Čeka odgovor', 'Waiting on others')} ({w.length})</h2>
                {w.length === 0 ? <p className="muted">{t('Trenutno ne čekaš nikoga.', 'Nothing currently blocked on others.')}</p> :
                    w.map((tk) => <DcTaskRow key={tk.id} task={tk} onComplete={onComplete} locale={locale} highlight="waiting" />)}
            </div>
        );
    }
    // today2 view
    const buckets = today ?? { overdue: [], today: [], tomorrow: [], this_week: [], later: [], no_date: [], waiting: [] };
    return (
        <div>
            <h2 style={{ color: BROWN, margin: '4px 0 12px' }}>🎯 Today2 / triaža</h2>
            <DcBucket title={t('Probijena', 'Overdue')} list={buckets.overdue} color="#b14040" onComplete={onComplete} locale={locale} />
            <DcBucket title={t('Danas', 'Today')} list={buckets.today} color="var(--ad-acc-3)" onComplete={onComplete} locale={locale} />
            <DcBucket title={t('Sutra', 'Tomorrow')} list={buckets.tomorrow} color="#1e40af" onComplete={onComplete} locale={locale} />
            <DcBucket title={t('Ovaj tjedan', 'This week')} list={buckets.this_week} color="var(--ad-ink)" onComplete={onComplete} locale={locale} />
            <DcBucket title={t('Kasnije', 'Later')} list={buckets.later} color="#888" onComplete={onComplete} locale={locale} collapseDefault />
            <DcBucket title={t('Bez datuma', 'No date')} list={buckets.no_date} color="#aaa" onComplete={onComplete} locale={locale} collapseDefault />
        </div>
    );
}

function DcBucket({ title, list, color, onComplete, locale, collapseDefault }: { title: string; list: DcTask[]; color: string; onComplete: (id: number, reopen?: boolean) => void; locale: Locale; collapseDefault?: boolean }) {
    const [open, setOpen] = useState(!collapseDefault);
    if (list.length === 0) return null;
    return (
        <div style={{ marginBottom: 14 }}>
            <button type="button" onClick={() => setOpen(!open)}
                style={{ background: 'transparent', border: 0, cursor: 'pointer', padding: '5px 0', color, fontSize: '0.84rem', fontWeight: 700, textTransform: 'uppercase' }}>
                {open ? '▾' : '▸'} {title} ({list.length})
            </button>
            {open ? list.map((t) => <DcTaskRow key={t.id} task={t} onComplete={onComplete} locale={locale} />) : null}
        </div>
    );
}

function DcTaskRow({ task, onComplete, locale, highlight }: { task: DcTask; onComplete: (id: number, reopen?: boolean) => void; locale: Locale; highlight?: 'waiting' }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const priColor = task.priority == 1 ? '#b14040' : task.priority == 2 ? 'var(--ad-acc-3)' : task.priority == 3 ? '#1e40af' : '#888';
    const due = task.due_at ? new Date(task.due_at) : null;
    return (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 10px', background: highlight === 'waiting' ? 'rgba(var(--ad-soft-rgb),0.08)' : '#fffdf6', border: '1px solid rgba(var(--ad-soft-rgb),0.2)', borderLeft: `4px solid ${priColor}`, borderRadius: 5, marginBottom: 4 }}>
            <input type="checkbox" checked={!!task.completed_at} onChange={() => onComplete(task.id, !!task.completed_at)} style={{ marginTop: 3 }} />
            <div style={{ flex: 1, fontSize: '0.86rem' }}>
                <div style={{ fontWeight: 600, color: BROWN }}>{task.title}</div>
                <div style={{ fontSize: '0.72rem', color: '#888', marginTop: 2 }}>
                    {due ? <>📅 {due.toLocaleString(locale === 'en' ? 'en-GB' : 'hr-HR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })} · </> : null}
                    {task.category ? <>🏷 {task.category} · </> : null}
                    {task.estimated_duration_minutes ? <>⏱ {task.estimated_duration_minutes}m · </> : null}
                    {task.waiting_for ? <span style={{ color: 'var(--ad-acc-3)' }}>⏸ {t('Čeka', 'Wait')}: {task.waiting_for}</span> : null}
                </div>
                {task.notes ? <div style={{ fontSize: '0.78rem', color: '#666', marginTop: 4, whiteSpace: 'pre-wrap' }}>{task.notes}</div> : null}
            </div>
        </div>
    );
}

function DcProjectDetail({ project, tasks, onComplete, locale }: { project: DcProject; tasks: DcTask[]; onComplete: (id: number, reopen?: boolean) => void; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const open = tasks.filter((x) => !x.completed_at);
    const done = tasks.filter((x) => x.completed_at);
    return (
        <div>
            <h2 style={{ color: BROWN, margin: '4px 0 4px' }}>📁 {project.title}</h2>
            <div style={{ fontSize: '0.78rem', color: '#888', marginBottom: 12 }}>
                {open.length} {t('aktivnih', 'active')} · {done.length} {t('odrađenih', 'done')} · status: <strong>{project.status}</strong>
            </div>
            {open.length === 0 && done.length === 0 ? <p className="muted">{t('Nema taskova u projektu.', 'No tasks in this project.')}</p> : null}
            {open.map((tk) => <DcTaskRow key={tk.id} task={tk} onComplete={onComplete} locale={locale} />)}
            {done.length > 0 ? <div style={{ marginTop: 14, fontSize: '0.72rem', color: '#888', textTransform: 'uppercase', fontWeight: 700 }}>{t('Odrađeno', 'Done')} ({done.length})</div> : null}
            {done.map((tk) => <DcTaskRow key={tk.id} task={tk} onComplete={onComplete} locale={locale} />)}
        </div>
    );
}

/** ── IntakeModal — DeChaos brzi unos (text ili voice → AI pipeline) ── */
function IntakeModal({ onClose, token, locale, isMobile, onSent }: { onClose: () => void; token: string | null; locale: Locale; isMobile: boolean; onSent?: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [text, setText] = useState('');
    const [busy, setBusy] = useState(false);
    const [recording, setRecording] = useState(false);
    const [result, setResult] = useState<any>(null);
    const [error, setError] = useState('');
    const recogRef = React.useRef<any>(null);

    const canSpeech = typeof window !== 'undefined' && ((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition);
    const startVoice = () => {
        const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        if (!SR) { setError(t('Tvoj preglednik ne podržava Web Speech API.', 'Your browser does not support Web Speech API.')); return; }
        const r = new SR();
        r.lang = locale === 'en' ? 'en-US' : 'hr-HR';
        r.continuous = true;
        r.interimResults = true;
        r.onresult = (e: any) => {
            let final = '';
            for (let i = 0; i < e.results.length; i++) {
                if (e.results[i].isFinal) final += e.results[i][0].transcript + ' ';
            }
            if (final) setText((t) => (t + ' ' + final).trim());
        };
        r.onerror = (e: any) => { setError('Speech error: ' + e.error); setRecording(false); };
        r.onend = () => setRecording(false);
        r.start();
        recogRef.current = r;
        setRecording(true);
    };
    const stopVoice = () => { try { recogRef.current?.stop(); } catch {} setRecording(false); };

    const send = async (source: 'text' | 'voice') => {
        if (!token || !text.trim()) return;
        setBusy(true); setError('');
        try {
            const r = await mutate<any>('/dechaos/intake', token, 'POST', { text: text.trim(), source });
            setResult(r); setText('');
            onSent?.();
        } catch (e) { setError(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 110, display: 'flex', justifyContent: 'center', alignItems: isMobile ? 'flex-end' : 'center' }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: '#fffdf6', padding: 16, borderRadius: isMobile ? '12px 12px 0 0' : 8, width: isMobile ? '100%' : 'min(540px, 96vw)', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 30px rgba(0,0,0,0.3)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <h3 style={{ margin: 0, color: '#b14040' }}>🧠 {t('Brzi unos u DeChaos', 'Quick intake to DeChaos')}</h3>
                    <span style={{ flex: 1 }} />
                    <button type="button" onClick={onClose} style={{ background: 'transparent', border: 0, fontSize: '1.4rem', cursor: 'pointer' }}>✕</button>
                </div>
                <p className="muted" style={{ fontSize: '0.78rem', marginTop: 0 }}>
                    {t('Upiši ili izdiktiraj — DeChaos AI će ekstraktirati taskove (alarm, lista, čekam, datum…) i smjestiti ih u triažu.', 'Type or dictate — DeChaos AI extracts tasks (alarm, list, waiting, date…) into your triage.')}
                </p>
                <textarea autoFocus value={text} onChange={(e) => setText(e.target.value)} rows={5}
                    placeholder={t('npr. "Sutra u 10 nazvati Slavena vezano za Allianz račun, podsjeti me u 9"…', 'e.g. "Tomorrow at 10 call Slaven re Allianz invoice, remind me at 9"…')}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid rgba(var(--ad-soft-rgb),0.4)', borderRadius: 6, fontSize: '0.96rem', resize: 'vertical' }} />
                <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    {canSpeech ? (
                        recording ? (
                            <button type="button" onClick={stopVoice} style={{ background: '#b14040', color: '#fff', border: 0, padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontWeight: 700, animation: 'pulse 1.2s infinite' }}>⏹ {t('Zaustavi snimanje', 'Stop recording')}</button>
                        ) : (
                            <button type="button" onClick={startVoice} style={{ background: 'transparent', color: '#b14040', border: '1px solid #b14040', padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontWeight: 700 }}>🎤 {t('Glasovni unos', 'Voice input')}</button>
                        )
                    ) : <span style={{ fontSize: '0.74rem', color: '#888' }}>🎤 {t('Speech API nedostupan u ovom pregledniku', 'Speech API unavailable')}</span>}
                    <div style={{ flex: 1 }} />
                    <button type="button" disabled={busy || !text.trim()} onClick={() => void send(recording ? 'voice' : 'text')}
                        style={{ background: '#1f7a36', color: '#fff', border: 0, padding: '8px 18px', borderRadius: 6, cursor: 'pointer', fontWeight: 700, opacity: busy || !text.trim() ? 0.5 : 1 }}>
                        {busy ? '⏳ ' + t('Šaljem…', 'Sending…') : '🚀 ' + t('Pošalji u DeChaos AI', 'Send to DeChaos AI')}
                    </button>
                </div>
                {error ? <div style={{ marginTop: 10, padding: 8, background: '#fde8e8', color: '#b14040', borderRadius: 5, fontSize: '0.82rem' }}>{error}</div> : null}
                {result ? (
                    <div style={{ marginTop: 12, padding: 10, background: 'rgba(31,122,54,0.08)', border: '1px solid #1f7a36', borderRadius: 6 }}>
                        <strong style={{ color: '#1f7a36' }}>✓ {t('Intake kreiran', 'Intake created')}</strong>
                        <div style={{ fontSize: '0.78rem', color: '#666', marginTop: 4 }}>
                            ID #{result.intake?.id} · {result.intake?.status} · AI: {result.ai_outcome}
                        </div>
                        {(result.ai_proposal_tasks ?? []).length > 0 ? (
                            <div style={{ marginTop: 6, fontSize: '0.84rem' }}>
                                <strong>{t('Predloženi taskovi:', 'Proposed tasks:')}</strong>
                                <ul style={{ margin: '4px 0 0 18px' }}>
                                    {(result.ai_proposal_tasks ?? []).map((tsk: any, i: number) => (
                                        <li key={i}>{tsk.title}{tsk.notes ? ' — ' + tsk.notes : ''}</li>
                                    ))}
                                </ul>
                            </div>
                        ) : null}
                        {(result.tasks_created ?? []).length > 0 ? (
                            <div style={{ marginTop: 6, fontSize: '0.84rem' }}>
                                <strong>{t('Kreirani taskovi:', 'Tasks created:')}</strong>
                                <ul style={{ margin: '4px 0 0 18px' }}>
                                    {(result.tasks_created ?? []).map((tsk: any) => (
                                        <li key={tsk.id}>{tsk.title}{tsk.due_at ? ' · ' + new Date(tsk.due_at).toLocaleString(locale === 'en' ? 'en-GB' : 'hr-HR') : ''}</li>
                                    ))}
                                </ul>
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

